
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { AlertCircle, RefreshCw, Trash } from "lucide-react";
import { purgeAllBlogPosts } from "@/utils/wordpress/database";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface PurgeImportControlProps {
  onPurgeComplete: () => void;
  disabled?: boolean;
}

export const PurgeImportControl = ({ onPurgeComplete, disabled = false }: PurgeImportControlProps) => {
  const [isPurging, setIsPurging] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handlePurgeClick = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmPurge = async () => {
    setIsPurging(true);
    setShowConfirmDialog(false);
    
    try {
      const result = await purgeAllBlogPosts();
      
      if (result.success) {
        toast({
          title: "Purge Successful",
          description: result.message,
        });
        onPurgeComplete();
      } else {
        toast({
          title: "Purge Failed",
          description: result.message,
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error during purge operation:", error);
      toast({
        title: "Purge Failed",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive"
      });
    } finally {
      setIsPurging(false);
    }
  };

  return (
    <>
      <div className="mb-6">
        <div className="flex items-center p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertCircle className="w-5 h-5 text-amber-600 mr-3 flex-shrink-0" />
          <div>
            <h3 className="font-medium text-amber-800">Database Management</h3>
            <p className="text-sm text-amber-700 mt-1">
              You can purge all existing blog posts before reimporting. This action cannot be undone.
            </p>
          </div>
        </div>
        
        <div className="mt-4 flex justify-end">
          <Button
            onClick={handlePurgeClick}
            disabled={disabled || isPurging}
            variant="outline"
            className="flex items-center gap-2 border-red-300 text-red-600 hover:bg-red-50"
          >
            {isPurging ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Purging...
              </>
            ) : (
              <>
                <Trash className="w-4 h-4" />
                Purge All Blog Posts
              </>
            )}
          </Button>
        </div>
      </div>
      
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete all blog posts
              and their associated data from the database.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmPurge}
              className="bg-red-600 hover:bg-red-700"
            >
              Yes, delete everything
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
